import React, { useEffect, useState } from "react";
import { api, formatDate } from "../../lib/api";
import { Trash2, Mail, Users, Copy } from "lucide-react";
import { toast } from "sonner";

export default function SubscribersTab() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/subscribers");
      setSubs(data);
    } catch {
      toast.error("Failed to load subscribers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const remove = async (s) => {
    if (!confirm(`Remove ${s.email} from the list?`)) return;
    try {
      await api.delete(`/admin/subscribers/${s.id}`);
      toast.success("Subscriber removed");
      await load();
    } catch {
      toast.error("Could not remove");
    }
  };

  const copyAll = async () => {
    const txt = subs.map((s) => s.email).join(", ");
    try {
      await navigator.clipboard.writeText(txt);
      toast.success(`Copied ${subs.length} email${subs.length === 1 ? "" : "s"}`);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div data-testid="subscribers-tab">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
        <div>
          <p className="font-script text-2xl" style={{ color: "var(--text-accent)" }}>
            your audience
          </p>
          <h1 className="font-display text-4xl" style={{ color: "var(--text-primary)" }}>
            Subscribers
          </h1>
        </div>
        <button
          onClick={copyAll}
          disabled={subs.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-50"
          style={{ border: "1px solid var(--border-soft)", color: "var(--text-primary)", backgroundColor: "#fff" }}
          data-testid="copy-all-emails-button"
        >
          <Copy size={14} />
          Copy all emails
        </button>
      </header>

      <div
        className="bg-white rounded-2xl p-5 md:p-7 mb-5 inline-flex items-center gap-4"
        style={{ border: "1px solid var(--border-soft)" }}
      >
        <div
          className="rounded-full p-3"
          style={{ backgroundColor: "var(--brand-peach)" }}
        >
          <Users size={20} style={{ color: "var(--brand-sage-dark)" }} />
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
            Total active
          </p>
          <p className="font-display text-3xl" style={{ color: "var(--text-primary)" }} data-testid="subscriber-total-count">
            {loading ? "…" : subs.length}
          </p>
        </div>
      </div>

      {loading ? (
        <p className="font-script text-xl" style={{ color: "var(--text-secondary)" }}>
          loading…
        </p>
      ) : subs.length === 0 ? (
        <p style={{ color: "var(--text-secondary)" }}>No subscribers yet.</p>
      ) : (
        <div
          className="bg-white rounded-2xl overflow-hidden"
          style={{ border: "1px solid var(--border-soft)" }}
        >
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: "var(--bg-secondary)" }}>
                <th className="text-left px-5 py-3 font-semibold">Email</th>
                <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Source</th>
                <th className="text-left px-5 py-3 font-semibold hidden sm:table-cell">Subscribed</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {subs.map((s) => (
                <tr key={s.id} style={{ borderTop: "1px solid var(--border-soft)" }} data-testid={`subscriber-row-${s.id}`}>
                  <td className="px-5 py-3 flex items-center gap-2">
                    <Mail size={14} style={{ color: "var(--text-secondary)" }} />
                    {s.email}
                  </td>
                  <td className="px-5 py-3 hidden md:table-cell" style={{ color: "var(--text-secondary)" }}>
                    {s.source}
                  </td>
                  <td className="px-5 py-3 hidden sm:table-cell" style={{ color: "var(--text-secondary)" }}>
                    {formatDate(s.created_at)}
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      onClick={() => remove(s)}
                      className="inline-flex items-center justify-center p-1.5 rounded-full"
                      style={{ color: "var(--brand-orange)" }}
                      aria-label="Remove subscriber"
                      data-testid={`remove-subscriber-${s.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
